import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { Field, FieldArray, Form, Formik } from 'formik';
import { get, debounce } from 'lodash';
import { Grid, Hidden } from '@material-ui/core';
import { object, string, mixed } from 'yup';
import { connect, useDispatch, useSelector } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { useTranslation } from '../../src/i18n';
import SubmitModalLayout from '../../components/SubmitModalLayout';
import { MEDIA_ATTRIBUTES_TYPES_IDS, TP, urlRegex, VISIBILITY } from '../../constants/consts';
import CountryCityBlock from '../../components/CountryCityBlock';
import EditModeCard from '../../components/EditModeCard';
import FormikTextField from '../../components/Fields/TextField';
import FormikPhoneField from '../../components/Fields/FormikPhoneField';
import { createEntity, getList, getVenuesAction, getWholeList, updateOne, useBufferValueAction } from '../App/actions';
import { COUNTRIES_ENDPOINT, MEDIA_ATTRIBUTES, VENUES } from '../../constants/endpointConstants';
import AddAnotherField from '../../components/AddAnotherField';
import { cleanVenueModalStoreAction, getCountriesAction, getImageFormats } from './actions';
import { selectCountriesOptions, selectImageFormatsNames } from './selectors';
import { useInjectReducer } from '../../store/injectReducer';
import reducer, { key } from './reducer';
import MediaSection, { getMedia } from './MediaSection';
import LogoUploadSection from './LogoUploadSection';
import { getOption } from '../../utils/common';
import TitleBlock from '../../components/EditComponents/TitleBlock';
import { selectBufferValueData, selectVenuesOptions } from '../App/selectors';
import FormikAutocompleteSelect from '../../components/Fields/FormikAutocompleteSelect';
import { isFocusedAddAnotherField } from '../../utils/editCommon';
import GoogleAddressField from './GoogleAddressField';

const useStyles = makeStyles(theme => ({
  form: {
    margin: theme.spacing(2),
  },
  fieldsBox: {
    padding: theme.spacing(2),
    margin: 0,
  },
  stagesInput: {
    maxWidth: 'unset',
    display: 'flex',
    [theme.breakpoints.down('xs')]: {
      display: 'block',
    },
  },
}));

const companyFields = {
  name: {
    name: 'name',
    label: `${TP}.VENUE_NAME`,
  },
  url: {
    name: 'website',
    label: `${TP}.m_WEBSITE`,
  },
  country: {
    name: 'country',
  },
  city: {
    name: 'city',
  },
  stages: {
    name: 'stages',
    label: `${TP}.FN_STAGE_NAME_ALIASES_TITLE`,
  },
  address: {
    name: 'address',
    label: `${TP}.m_ADDRESS`,
  },
  email: {
    name: 'email',
    label: `${TP}.m_EMAIL`,
  },
  phone: {
    name: 'phone',
    label: `${TP}.m_REGISTRATION_PHONENUMBER`,
  },
};

const { name, url, country, city, address, email, phone, stages } = companyFields;

const getStageFieldsValues = stage => ({ ...stage, capacity: stage.capacity || undefined, order: stage?.id });
const stageDefaultValue = index => ({ name: '', capacity: undefined, order: index });

const getInitialValues = (incomingData, isFormValues) => {
  const valueOption = v => (isFormValues ? v : getOption(v, 'id', 'name', {}, true));
  return {
    [name.name]: incomingData.name,
    [country.name]: incomingData?.country ? valueOption(incomingData?.country) : undefined,
    [city.name]: incomingData?.city ? valueOption(incomingData?.city) : undefined,
    [address.name]: incomingData?.address || '',
    [stages.name]:
      incomingData?.stages?.length === 0 || !incomingData?.stages
        ? [stageDefaultValue(0)]
        : incomingData?.stages.map(s => getStageFieldsValues(s)),
    [email.name]: incomingData?.email || '',
    [phone.name]: incomingData?.phone || undefined,
    [url.name]: incomingData?.url || '',
  };
};

const VenueModal = ({
  className,
  onClose,
  incomingData,
  isFormValues,
  callback,
  venues,
  entityVenues,
  isSearchable,
  hostName,
  imageFormats,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  useInjectReducer([{ key, reducer }]);

  const countriesOptions = useSelector(selectCountriesOptions());
  const bufferValue = useSelector(selectBufferValueData());
  const { t } = useTranslation();

  const formRef = React.createRef(null);
  const [logo, setLogo] = useState(null);
  const [logoSrc, setLogoSrc] = useState(null);
  const id = get(incomingData, 'id');
  const companyVenuesIds = entityVenues.map(el => el.id);

  useEffect(() => {
    getMedia(dispatch, id);
    setLogoSrc(get(incomingData, 'logo.medium', null));
    if (!imageFormats.length) {
      dispatch(
        getWholeList({
          endpoint: MEDIA_ATTRIBUTES,
          sagaRoutine: getImageFormats,
          params: {
            queryParams: {
              attribute_type_id: MEDIA_ATTRIBUTES_TYPES_IDS.IMAGE_FORMAT,
            },
          },
        }),
      );
    }
    if (!countriesOptions.length) {
      dispatch(
        getWholeList({
          endpoint: COUNTRIES_ENDPOINT,
          sagaRoutine: getCountriesAction,
        }),
      );
    }
    return () => {
      dispatch(cleanVenueModalStoreAction());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createVenue = values => {
    const getIdFromValue = field => (get(values, `${field}.value`) ? { id: get(values, `${field}.value`) } : null);
    const notEmptyNamesStages = get(values, stages.name, []).reduce((result, stage) => {
      if (stage?.name) {
        return [...result, { ...stage, capacity: +stage?.capacity }];
      }
      return result;
    }, []);

    const params = {
      // eslint-disable-next-line no-underscore-dangle
      ...(!isSearchable || values.name.__isNew__
        ? { name: get(values, isSearchable ? `${name.name}.value` : name.name) }
        : {}),
      url: get(values, url.name),
      country: getIdFromValue(country.name),
      city: getIdFromValue(city.name),
      address: get(values, address.name),
      email: get(values, email.name),
      phone: get(values, phone.name, ''),
      stages: notEmptyNamesStages,
      [hostName]: get(incomingData, hostName, []),
    };

    const requestParams = {
      endpoint: VENUES,
      sagaRoutine: useBufferValueAction,
      params,
      callback,
    };

    if (logo) {
      params.logo = { file: `@${logo.filename}` };
      requestParams.files = { [logo.filename]: logo.file };
    }

    const venueId = id || values[name.name].id;
    if (venueId) {
      params.id = venueId;
      requestParams.id = venueId;
      dispatch(updateOne(requestParams));
      return;
    }
    dispatch(createEntity(requestParams));
  };

  const initialValues = getInitialValues(incomingData, isFormValues);

  const getVenues = debounce(query => {
    dispatch(
      getList({
        endpoint: VENUES,
        sagaRoutine: getVenuesAction,
        params: {
          queryParams: {
            query,
            show_invalid: true,
            visibility: VISIBILITY.ALL,
          },
        },
      }),
    );
  }, 300);

  const updateFormFields = selectedVenue => {
    if (selectedVenue?.id) {
      const updatedValues = getInitialValues({ ...selectedVenue, name: selectedVenue });
      formRef.current.setValues(updatedValues);
      setLogoSrc(get(selectedVenue, 'logo.medium', null));
    } else {
      formRef.current.setFieldValue(name.name, selectedVenue || undefined);
    }
  };

  const getVenueLocation = v => {
    const venueName = v.label;
    const countryName = v?.country?.name;
    const cityName = v?.city?.name;
    const locationString = countryName && cityName ? `(${countryName}, ${cityName})` : `(${countryName || cityName})`;
    return `${venueName}${countryName || cityName ? locationString : ''}`;
  };

  const venueNameSearchFieldProps = {
    options: venues.options.map(el => ({ ...el, label: getVenueLocation(el) })),
    onInputTextChange: getVenues,
    minSearchCharsLength: 3,
    instanceId: 'venue_modal_company',
    isLoading: venues.loading,
    onChange: updateFormFields,
    isCreatable: true,
    preventDefaultOnChange: true,
    isClearable: true,
    withFiltering: true,
    filterOption: val => !companyVenuesIds.includes(val.value),
  };

  return (
    <SubmitModalLayout
      className={className}
      isOpened
      onClose={onClose}
      title1={t(`${TP}.FN_ADD_NEW_VENUE`)}
      submitFunc={() => {
        get(formRef, 'current.submitForm', () => {})();
      }}
      isLoading={bufferValue?.loading}
    >
      <EditModeCard className={classes.form}>
        <Formik
          initialValues={initialValues}
          onSubmit={createVenue}
          validationSchema={AddNewVenueSchema}
          innerRef={formRef}
          enableReinitialize
        >
          {({ values, setFieldValue }) => (
            <Form>
              <Grid container spacing={2} className={classes.fieldsBox} alignItems="flex-start">
                <Grid item sm={12} md={2}>
                  <LogoUploadSection src={logoSrc} setLogo={setLogo} formats={imageFormats} />
                </Grid>
                <Grid container item sm={12} md={10} spacing={2} className={classes.fieldsBox} alignItems="flex-start">
                  <Grid item sm={6} xs={12}>
                    <Field
                      name={name.name}
                      component={isSearchable ? FormikAutocompleteSelect : FormikTextField}
                      label={t(name.label)}
                      {...(isSearchable ? venueNameSearchFieldProps : {})}
                    />
                  </Grid>
                  <Grid item sm={6} xs={12} />
                  <Grid item sm={12} xs={12}>
                    <FieldArray name={stages.name}>
                      {arrayHelpers => {
                        const stagesValues = get(values, stages.name, []);
                        return (
                          <>
                            {stagesValues.map((stage, index) => {
                              const fieldName = `${stages.name}[${index}].name`;
                              const fieldCapacity = `${stages.name}[${index}].capacity`;
                              return (
                                <AddAnotherField
                                  values={stagesValues}
                                  removeFunc={() => arrayHelpers.remove(index)}
                                  addFunc={() => arrayHelpers.push(stageDefaultValue(index))}
                                  isLast={index + 1 === stagesValues.length}
                                  addDisabled={stagesValues.some(s => !s?.name)}
                                  addButtonPosition="bottom"
                                  key={`stage-${stage?.order}`}
                                  inputClassName={classes.stagesInput}
                                >
                                  <Grid item sm={6} xs={12}>
                                    <Field
                                      autoFocus={isFocusedAddAnotherField(index, stagesValues)}
                                      name={fieldName}
                                      component={FormikTextField}
                                      label={t(`${TP}.m_ROOM`)}
                                    />
                                  </Grid>
                                  <Hidden xsDown>
                                    <Grid item xs={1} />
                                  </Hidden>
                                  <Grid item sm={5} xs={12}>
                                    <Field
                                      name={fieldCapacity}
                                      component={FormikTextField}
                                      label={t(`${TP}.m_SEATINGCAPACITY`)}
                                      type="number"
                                    />
                                  </Grid>
                                </AddAnotherField>
                              );
                            })}
                          </>
                        );
                      }}
                    </FieldArray>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Field
                      component={GoogleAddressField}
                      setFieldValue={setFieldValue}
                      countriesOptions={countriesOptions}
                      formValues={values}
                      name={address.name}
                      label={t(address.label)}
                    />
                  </Grid>
                  <Grid item xs={6} />
                  <CountryCityBlock
                    countriesOptions={countriesOptions}
                    formValues={values}
                    setFieldValue={setFieldValue}
                  />
                  {id && (
                    <Grid item xs={12}>
                      <MediaSection id={id} formats={imageFormats} />
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <TitleBlock
                      title={t(`${TP}.VENUE_CONTACT_INFORMATION`)}
                      subtitle={t(`${TP}.VENUE_CONTACT_INFORMATION_SUBTITLE`)}
                    />
                  </Grid>
                  <Grid item md={4} sm={6} xs={12}>
                    <Field name={email.name} component={FormikTextField} label={t(email.label)} />
                  </Grid>
                  <Grid item md={4} sm={6} xs={12}>
                    <Field name={phone.name} label={t(phone.label)} component={FormikPhoneField} />
                  </Grid>
                  <Grid item md={4} sm={6} xs={12}>
                    <Field name={url.name} component={FormikTextField} label={t(url.label)} />
                  </Grid>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </EditModeCard>
    </SubmitModalLayout>
  );
};

VenueModal.propTypes = {
  className: PropTypes.string,
  onClose: PropTypes.func,
  incomingData: PropTypes.shape({
    name: PropTypes.string,
    country: PropTypes.object,
    city: PropTypes.object,
  }),
  isFormValues: PropTypes.bool,
  isSearchable: PropTypes.bool,
  hostName: PropTypes.string,
  entityVenues: PropTypes.array,
  imageFormats: PropTypes.arrayOf(PropTypes.string),
};

VenueModal.defaultProps = {
  incomingData: {
    name: '',
    country: null,
    city: null,
  },
  isFormValues: false,
  isSearchable: false,
  hostName: 'companies',
  entityVenues: [],
};

const mapStateToProps = createStructuredSelector({
  venues: selectVenuesOptions(),
  imageFormats: selectImageFormatsNames(),
});
const withConnect = connect(mapStateToProps, null);

export default withConnect(VenueModal);

const AddNewVenueSchema = () =>
  object().shape({
    [name.name]: mixed()
      .nullable()
      .required(`${TP}.FN_REQUIRED_FIELD`),
    [url.name]: string()
      .matches(urlRegex, `${TP}.FN_WEB_LINK_INVALID`)
      .nullable(),
    [email.name]: string()
      .nullable()
      .email(`${TP}.m_MAILNONVALID`),
    [country.name]: object()
      .nullable()
      .required(`${TP}.FN_REQUIRED_FIELD`),
    [city.name]: object()
      .nullable()
      .required(`${TP}.FN_REQUIRED_FIELD`),
    [address.name]: string().nullable(),
    [phone.name]: string().nullable(),
  });
